import { createRouter, createWebHistory } from 'vue-router'
import { beforeCalendarEnter } from '@/helpers/calendar'
import { useUserStore } from '@/stores/UserStore'
import { getStoredSession, isSessionExpired } from '@/helpers/session'

import siteConfig from '@/config.json'
import { capitalizeFirstLetter } from '@/helpers/text'

export function getHomePath() {
  if (siteConfig.modules.classes) {
    return siteConfig.modules.exhibitions && siteConfig.features.exhibitionsBeforeClasses
      ? '/exhibitions'
      : siteConfig.modules.events && siteConfig.features.eventsBeforeClasses
      ? '/events'
      : '/classes'
  }

  return siteConfig.key === 'aws' ? '/exhibitions' : null
}

export function goHome(router) {
  const homePath = getHomePath()
  if (homePath) {
    return router.push(homePath)
  }
  window.location.href = siteConfig.org.website
  // empty promise so chained reload doesn't run
  return new Promise(() => {})
}

const MODULE_ROUTES = {
  classes: [
    {
      path: '/',
      redirect: getHomePath(),
    },
    {
      path: '/classes',
      name: 'Classes: index',
      component: () => import('./RouterView.vue'),
      meta: {
        title: 'Classes',
        ...(siteConfig.features.separateClassesByDivision
          ? { division: siteConfig.features.separateClassesByDivision.classes }
          : {}),
      },
      children: [
        {
          path: '',
          name: 'Store Classes',
          component: () => import('../views/Classes/index.vue'),
        },
        {
          path: ':classId',
          name: 'Class Details',
          component: () => import('../views/Classes/ClassDetails.vue'),
          props: true,
        },
      ],
    },
    ...(siteConfig.features.separateClassesAndCamps
      ? [
          {
            path: '/camps',
            name: 'Camps: index',
            component: () => import('./RouterView.vue'),
            meta: { division: 'camp' },
            children: [
              {
                path: '',
                name: 'Store Camps',
                component: () => import('../views/Classes/index.vue'),
              },
              {
                path: ':classId',
                name: 'Camp Details',
                component: () => import('../views/Classes/ClassDetails.vue'),
                props: true,
              },
            ],
          },
        ]
      : []),
    ...(siteConfig.features.separateClassesByDivision
      ? Object.entries(siteConfig.features.separateClassesByDivision).map(([label, divisions]) => ({
          path: `/${label.toLowerCase()}`,
          name: `${label} index`,
          component: () => import('./RouterView.vue'),
          meta: {
            title: capitalizeFirstLetter(label),
            division: divisions,
          },
          children: [
            {
              path: '',
              name: `${label} List`,
              component: () => import('../views/Classes/index.vue'),
            },
            {
              path: ':classId',
              name: `${label} Details`,
              component: () => import('../views/Classes/ClassDetails.vue'),
              props: true,
            },
          ],
        }))
      : []),
  ],
  events: [
    {
      path: '/events',
      name: 'Events: index',
      meta: {
        title: 'Events',
      },
      component: () => import('./RouterView.vue'),
      children: [
        {
          path: '',
          name: 'Store Events',
          component: () => import('../views/Events/index.vue'),
        },
        {
          path: ':eventId',
          name: 'Event Details',
          component: () => import('../views/Events/EventDetails.vue'),
          props: true,
        },
      ],
    },
  ],
  memberships: [
    {
      path: '/memberships',
      name: 'Memberships',
      meta: {
        title: 'Memberships',
      },
      component: () => import('../views/Memberships/index.vue'),
    },
  ],
  calendar: [
    {
      path: '/calendar/:timeUnit?/:date?',
      name: 'Calendar',
      component: () => import('../views/Calendar/index.vue'),
      props: true,
      beforeEnter: beforeCalendarEnter,
    },
  ],
  giftCertificates: [
    {
      path: '/gift-certificates',
      name: 'Gift Certificate',
      component: () => import('../views/Gift/index.vue'),
    },
  ],
  exhibitions: [
    {
      path: '/exhibitions',
      name: 'Exhibitions',
      component: () => import('./RouterView.vue'),
      children: [
        {
          path: '',
          name: 'Store Exhibitions',
          component: () => import('../views/Exhibitions/index.vue'),
        },
        {
          path: ':exhibitionId',
          name: 'Exhibition Details',
          component: () => import('../views/Exhibitions/ExhibitionDetails.vue'),
          props: true,
        },
      ],
    },
    {
      path: '/submissions',
      name: 'Submissions: index',
      component: () => import('../views/SubmitArtwork/index.vue'),
      children: [
        {
          path: 'new/:exhibitionId',
          name: 'Submissions: Add Entries',
          component: () => import('../views/SubmitArtwork/AddEntry.vue'),
        },
        {
          path: 'payment',
          name: 'Submissions: Enter Payment',
          component: () => import('../views/SubmitArtwork/EnterPayment.vue'),
        },
        {
          path: 'confirmation',
          name: 'Submissions: Confirmation',
          component: () => import('../views/SubmitArtwork/Confirmation.vue'),
        },
      ],
    },
  ],
  instructors: [
    {
      path: '/instructors',
      name: 'Instructors',
      meta: {
        title: 'Instructors',
      },
      component: () => import('./RouterView.vue'),
      children: [
        {
          path: '',
          name: 'Store Instructors',
          component: () => import('../views/Instructors/index.vue'),
        },
        {
          path: ':instructorId',
          name: 'Instructor Profile',
          component: () => import('../views/Instructors/InstructorProfile.vue'),
          props: true,
        },
      ],
    },
  ],
  shop: [
    ...(siteConfig.features?.shop?.alternativeShop
      ? [
          {
            path: '/shop',
            name: 'Shop',
            component: () => import('../views/ShopAlt/index.vue'),
            children: [
              { name: 'Shop', path: '', component: () => import('../views/ShopAlt/AllProducts.vue') },
              {
                path: 'products',
                component: () => import('./RouterView.vue'),
                children: [
                  { name: 'Shop All', path: '', component: () => import('../views/ShopAlt/AllProducts.vue') },
                  {
                    name: 'Shop Product Details',
                    path: ':id',
                    component: () => import('../views/ShopAlt/ProductDetails.vue'),
                    meta: { hideHeroImage: true },
                  },
                ],
              },
            ],
          },
        ]
      : [
          {
            path: '/shop',
            name: 'Shop',
            component: () => import('./RouterView.vue'),
            children: [
              {
                path: '',
                name: 'Store Shop',
                component: () => import('../views/Shop/index.vue'),
              },
              {
                path: ':productId',
                name: 'Product Details',
                component: () => import('../views/Shop/Details/index.vue'),
                props: true,
              },
            ],
          },
        ]),
  ],
  donations: [
    {
      path: '/donate',
      name: 'Store Donate',
      meta: {
        title: 'Donate',
        requireAuth: siteConfig.features.donateRequireAuth,
      },
      component: () => import('../views/Donate/index.vue'),
    },
    {
      path: '/donate/confirmation',
      name: 'Store Donate Confirmation',
      meta: {
        title: 'Donate Confirmation',
      },
      component: () => import('../views/Donate/confirmation.vue'),
    },
  ],
  rentals: [
    {
      path: siteConfig.features?.navigation?.paths?.rentals || '/rentals',
      name: siteConfig.copy?.rentals?.title || 'Rentals',
      meta: {
        title: siteConfig.copy?.rentals?.title || 'Rentals',
      },
      component: () => import('./RouterView.vue'),
      children: [
        {
          path: '',
          name: siteConfig.copy?.rentals?.indexTitle || 'Rent Studios and Equipment',
          component: () => import('../views/Rentals/index.vue'),
        },
        {
          path: ':rentalId/:date?',
          name: siteConfig.copy?.rentals?.detailsTitle || 'Rental Details',
          component: () => import('../views/Rentals/RentalDetails.vue'),
          props: true,
        },
      ],
    },
  ],
}

const ALWAYS_TRUE_MODULES = [] // ['calendar'] // , 'shop'

// XXX TODO Make routes dynamic based on siteConfig and configured features/modules

// const routeGuard = (to, from, next) => {
//   const { isAuthenticated, loading, loginWithRedirect } = authPlugin

//   const verify = () => {
//     // If the user is authenticated, continue with the route
//     if (isAuthenticated.value) {
//       console.log('routeGuard: user is authenticated')
//       return next()
//     }

//     // Otherwise, log in
//     console.log('routeGuard: redirecting to login, targetUrl', to.fullPath)
//     loginWithRedirect({ appState: { targetUrl: to.fullPath } })
//   }

//   // If loading has already finished, check our auth state using `fn()`
//   if (!loading.value) {
//     return verify()
//   }

//   // Watch for the loading property to change before we check isAuthenticated
//   watchEffect(() => {
//     if (loading.value === false) {
//       return verify()
//     }
//   })
// }

const routes = []

addModuleRoutes()

if (!siteConfig.modules.classes) {
  if (siteConfig.key === 'aws') {
    routes.push({
      path: '/',
      redirect: '/exhibitions',
    })
  } else {
    routes.push({
      path: '/',
      redirect: (to) => {
        window.location.href = siteConfig.org.website
      },
    })
  }
}

// Sign In & Password Reset Flow
routes.push({
  path: '/sign-in',
  name: 'Sign In',
  component: () => import('../views/Auth/SignIn.vue'),
})

routes.push({
  path: '/forgot-password',
  name: 'Forgot Password',
  component: () => import('../views/Auth/ForgotPassword.vue'),
})

routes.push({
  path: '/reset-password/:token',
  name: 'Reset Password',
  component: () => import('../views/Auth/ResetPassword.vue'),
  props: true,
})

routes.push({
  path: '/activate/:token',
  name: 'Activate Account',
  component: () => import('../views/Auth/ResetPassword.vue'),
  props: true,
})

// Create New Account Flow
routes.push({
  path: '/create',
  name: 'Create: index',
  component: () => import('../views/Auth/Create/index.vue'),
  children: [
    {
      path: 'account',
      name: 'Create: Account Info',
      component: () => import('../views/Auth/Create/AccountInfo.vue'),
    },
    ...(siteConfig.features?.selfRegistrationOnly
      ? []
      : [
          {
            path: 'attendee',
            name: 'Create: Attendee Profiles',
            component: () => import('../views/Auth/Create/AttendeeProfiles.vue'),
            meta: { requireAuth: true },
          },
        ]),
    {
      path: 'payment',
      name: 'Create: Payment Methods',
      component: () => import('../views/Auth/Create/PaymentMethods.vue'),
      meta: { requireAuth: true },
    },
  ],
})

// Account Overview Flow
routes.push({
  path: '/account',
  name: 'Account: index',
  component: () => import('../views/Account/index.vue'),
  meta: { requireAuth: true },
  children: [
    {
      path: 'info',
      name: 'Account: Account Info',
      component: () => import('../views/Account/AccountInfo.vue'),
    },
    ...(siteConfig.features?.selfRegistrationOnly
      ? []
      : [
          {
            path: 'household-members',
            name: 'Account: Household Members',
            component: () => import('../views/Account/HouseholdMembers.vue'),
          },
        ]),
    {
      path: 'classes',
      component: () => import('./RouterView.vue'),
      children: [
        {
          path: '',
          name: 'Account: Instructor Classes',
          component: () => import('../views/Account/MyClasses/index.vue'),
        },
        {
          path: ':classId/roster',
          name: 'Account: Roster Details',
          component: () => import('../views/Account/MyClasses/RosterDetails.vue'),
          props: true,
        },
      ],
    },
    {
      path: 'membership',
      name: 'Account: Membership Status',
      component: () => import('../views/Account/MembershipStatus.vue'),
    },
    {
      path: 'submissions',
      name: 'Account: Submission History',
      component: () => import('../views/Account/SubmissionHistory.vue'),
    },
    {
      path: 'profiles',
      name: 'Account: Saved Profiles',
      component: () => import('../views/Account/SavedProfiles.vue'),
    },
    {
      path: 'payment-methods',
      name: 'Account: Payment Methods',
      component: () => import('../views/Account/PaymentMethods.vue'),
    },
    {
      path: 'order-history',
      name: 'Account: Order History index',
      component: () => import('./RouterView.vue'),
      children: [
        {
          path: '',
          name: 'Account: Order History Table',
          component: () => import('../views/Account/OrderHistory/index.vue'),
        },
        {
          path: ':orderId',
          name: 'Account: Order Details',
          component: () => import('../views/Account/OrderHistory/OrderDetails.vue'),
          props: true,
        },
      ],
    },
    {
      path: 'reports',
      name: 'Account: Reports',
      component: () => import('../views/Account/Reports.vue'),
    },
    {
      path: 'jury',
      name: 'Account: Jury Dashboard',
      component: () => import('./RouterView.vue'),
      meta: { requireAuth: true },
      children: [
        {
          path: '',
          name: 'Account: Jury Assignments',
          component: () => import('../views/Account/Jury/index.vue'),
        },
        {
          path: 'exhibit/:exhibitId',
          name: 'Account: Jury Voting',
          component: () => import('../views/Account/Jury/JuryExhibit.vue'),
        },
      ],
    },
  ],
})

// Checkout Flow
routes.push({
  path: '/checkout',
  name: 'Checkout: index',
  meta: { requireAuth: true },
  component: () => import('../views/Checkout/index.vue'),
  children: [
    {
      path: 'cart',
      name: 'Checkout: Review Cart',
      component: () => import('../views/Checkout/ReviewCart/index.vue'),
    },
    {
      path: 'terms',
      name: 'Checkout: Terms Acceptance',
      component: () => import('../views/Checkout/TermsAcceptance.vue'),
    },
    {
      path: 'payment',
      name: 'Checkout: Payment Info',
      component: () => import('../views/Checkout/PaymentInfo.vue'),
    },
    {
      path: 'confirmation',
      name: 'Checkout: Confirmation',
      component: () => import('../views/Checkout/Confirmation.vue'),
    },
  ],
})

if (siteConfig.key === 'snowfarm') {
  // if (!siteConfig.modules.classes) {
  //   routes.push({
  //     path: '/',
  //     // redirect: '/onboarding/test'
  //     redirect: (to) => {
  //       window.location.href = 'https://snowfarm.org'
  //     }
  //   })
  // }

  // Instructor Onboarding
  routes.push({
    path: '/onboarding/:year/:instructorId',
    name: 'Onboarding Root With Year',
    component: () => import('../views/InstructorOnboarding/index.vue'),
    children: [
      {
        path: 'contract',
        component: () => import('../views/InstructorOnboarding/SignContract.vue'),
      },
      {
        path: 'confirmation',
        component: () => import('../views/InstructorOnboarding/SignContract.vue'),
      },
      {
        path: 'status',
        component: () => import('../views/InstructorOnboarding/Status.vue'),
      },
      {
        path: 'forms/:form',
        component: () => import('../views/InstructorOnboarding/Form.vue'),
      },
      {
        path: 'forms/:form/:recordId',
        component: () => import('../views/InstructorOnboarding/Form.vue'),
      },
    ],
  })

  // Instructor Onboarding
  routes.push({
    path: '/onboarding/:instructorId',
    name: 'Onboarding Root',
    component: () => import('../views/InstructorOnboarding/index.vue'),
    children: [
      {
        path: 'contract',
        component: () => import('../views/InstructorOnboarding/SignContract2023.vue'),
      },
      {
        path: 'confirmation',
        component: () => import('../views/InstructorOnboarding/SignContract2023.vue'),
      },
      {
        path: 'status',
        componeonent: () => import('../views/InstructorOnboarding/Status.vue'),
      },
      {
        path: 'forms/:form',
        component: () => import('../views/InstructorOnboarding/Form.vue'),
      },
      {
        path: 'forms/:form/:recordId',
        component: () => import('../views/InstructorOnboarding/Form.vue'),
      },
    ],
  })
}

routes.push({
  path: '/forms/:slug',
  name: 'Form',
  component: () => import('../views/Forms/index.vue'),
})

// if (siteConfig.key === 'kershaw') { // XXX
routes.push({
  path: '/reports/:slug',
  name: 'Report',
  meta: { hideNav: true, requireAuth: true },
  component: () => import('../views/Reports/index.vue'),
})
// }

if (siteConfig.key === 'kirkland') {
  routes.push({
    path: '/docs',
    component: () => import('./RouterView.vue'),
    children: [
      {
        path: 'contracts',
        name: 'Docs: Instructor Contract',
        component: () => import('../views/Docs/InstructorContract/index.vue'),
        children: [
          {
            path: ':instructorId',
            component: () => import('../views/Docs/InstructorContract/Contract.vue'),
          },
          {
            path: ':instructorId/confirmation',
            component: () => import('../views/Docs/InstructorContract/Confirmation.vue'),
          },
        ],
      },
    ],
  })
}

routes.push({
  path: '/cart-add/:userId/:itemType/:itemId',
  name: 'CartAdd',
  meta: { hideNav: true },
  component: () => import('../views/Account/CartAdd.vue'),
})

routes.push({
  path: '/:catchAll(.*)',
  name: 'Not Found',
  // meta: {hideNav: true},
  component: () => import('../views/NotFound/index.vue'),
})

routes.push({
  path: '/maintenance',
  name: 'Maintenance',
  meta: { hideNav: true },
  component: () => import('../views/Maintenance/index.vue'),
})

routes.push({
  path: '/terms',
  name: 'Terms Acceptance',
  meta: { returnToHome: true, requireAuth: true },
  component: () => import('../views/Checkout/TermsAcceptance.vue'),
})

routes.push({
  path: '/pay-now',
  name: 'OneOffPayment',
  component: () => import('../views/PayNow/index.vue'),
  meta: { requireAuth: true },
})

routes.push({
  path: '/pay-now/confirmation',
  name: 'Pay Now Confirmation',
  component: () => import('../views/PayNow/confirmation.vue'),
})

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

// Example
// router.beforeEach((to, from, next) => {
//   // we wanted to use the store here
//   if (store.isLoggedIn) next()
//   else next('/login')
// })

router.beforeEach((to, from, next) => {
  // console.log('beforeEach to', to)
  const userStore = useUserStore()

  if (siteConfig.maintenanceMode && to.name !== 'Maintenance') {
    return next({ name: 'Maintenance' })
  }
  if (!siteConfig.maintenanceMode && to.name === 'Maintenance') {
    return next({ path: '/' })
  }

  if (to.meta.requireAuth) {
    const session = getStoredSession()

    if (!userStore.loggedIn && (!session || isSessionExpired(session))) {
      userStore.redirectTo = to.fullPath
      return next('/sign-in?backTo=' + to.fullPath)
    }
  }

  next()
})

router.afterEach((to) => {
  // console.log('afterEach to', to)
  const pageview = {
    event: 'pageview',
    pagePath: window.location.pathname,
    pageTitle: siteConfig.org.name,
  }
  // console.log('pageview', pageview)

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(pageview)
})

export default router

function addModuleRoutes() {
  Object.entries(MODULE_ROUTES).forEach(([module, routeList]) => {
    if (siteConfig.modules[module] || ALWAYS_TRUE_MODULES.includes(module)) {
      routeList.forEach((route) => {
        if (route.children) {
          route.children = route.children.map((child) => ({
            ...child,
            meta: { title: route.meta?.title || '' },
          }))
        }
        routes.push(route)
      })
    }
  })
}
